/**
 * Router definition for the Stripe Connect module.
 *
 * /stripe-connect       → redirects to /stripe-connect/list
 * /stripe-connect/list  → StripeConnectList (cards per Stripe processor)
 */

import {
  LoaderFunctionArgs,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useEffect } from "react";
import { Breadcrumb } from "../../components/Breadcrumb";
import { StripeConnectList, stripeConnectLoader } from "./StripeConnectList";
import { requireAuth } from "../auth/RequireAuth";
import { userStore } from "../../store/user";
import { stripeConnectAccessFor } from "./access";

const StripeConnectIndex = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Match the pattern used by other module index components (e.g.
  // payment_processor_config/index.tsx): bounce the bare module path to /list.
  useEffect(() => {
    if (location.pathname === "/stripe-connect") {
      navigate("/stripe-connect/list", { replace: true });
    }
  }, [location.pathname, navigate]);

  return <Outlet />;
};

// Hiding the nav link is not a control — /stripe-connect/list is guessable, so
// the route re-checks on every entry. Mirrors payment_processor_config.
const guardLoader = (loader?: (args: LoaderFunctionArgs) => unknown) =>
  requireAuth(undefined, async (args: LoaderFunctionArgs) => {
    if (!stripeConnectAccessFor(userStore.getState().user).access) {
      return redirect("/");
    }
    return loader ? await loader(args) : null;
  });

export const stripeConnectRouter = {
  path: "/stripe-connect",
  id: "stripe-connect",
  element: <StripeConnectIndex />,
  loader: guardLoader(),
  handle: {
    crumb: () => <Breadcrumb name="Stripe Connect" href="/stripe-connect/list" />,
  },
  children: [
    {
      path: "list",
      id: "stripe-connect-list",
      element: <StripeConnectList />,
      loader: guardLoader(stripeConnectLoader),
    },
  ],
};
